/** @jsx jsx */
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { css, jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import _ from 'lodash'
import useRootStore from '@store/useRootStore'
import { EMPTY_ICON } from '@constants/index'
import { AdvEmpty } from '@components/AdvEmpty'
import { ListView } from 'antd-mobile'
import { COMMUNICATION_BLOCK_TYPE, EXCEPTIONAL_UNIQUE_ID, FROM_VIEW_TYPE } from '@v2/constants/common'
import { runInAction } from 'mobx'
import { Schema$UserPick, Schema$UserPickExtend } from '@interface/user-pick'
import { Schema$CompanyPick } from '@interface/company-pick'
import {
  CompanyPickLikeActivityListItem,
} from '@views/MyPage/UserActivity/LikeActivity/CompanyPickLikeActivity/CompanyPickLikeActivityListItem'
import { UserPickItem } from '@views/Home/UserPick/Components/UserPickItem'
import { isMobile } from 'react-device-detect'
import { UserPickWriteButton } from '@views/Home/UserPick/Components/UserPickWriteButton'
import { MatchUpDetailPickWrap } from '@views/Matchup/MatchUpDetail/index.style'
import { MatchTrack } from '@store/matchupStore'
import { PAGE_TYPE } from '@interface/common'
import { GAME_TYPE } from '@game-type/game-market-types'
import { UserPickStore } from '@store/userPickStore'

const ListViewStyle = css`
    position: static !important;

    .am-list-body {
        margin-bottom: 90px;
    }
`
export const Pick: React.FC = observer(props => {
  // ********************* 분석글(프리미엄 분석가/나도승부사) *********************
  const { userPickStore, companyPickStore, matchupStore } = useRootStore()
  const { detailUserPicks, detailUserPickRecentHitRateMap } = userPickStore
  const { companyPicks } = companyPickStore
  const { detailTrack, pageType, currentGameKey } = matchupStore
  const { matchId } = detailTrack?.track ?? ({} as MatchTrack)
  const [picks, setPicks] = useState([])
  // const matchDetailWwrapRef = useRef(document.getElementById('matchDetailWrap'))

  const userPickStoreRef = useRef<UserPickStore>(userPickStore)

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1, row2) => row1 !== row2,
  })

  const listScrollHandler = () => {
    if (!userPickStoreRef.current.detailHasMoreUserPicks || userPickStoreRef.current.isFetchUserPicksLoading) {
      return
    }

    const matchDetailWrap = document.getElementById('matchDetailWrap')
    // 전체 높이 (scrollHeight) - 화면 높이 (clientHeight) - 마지막 화면에 스크롤 지점 (화면 높이/2) < 현재 스크롤 위치
    // 전체 높이 - 화면 높이 = 최대 scrollTop 에 근접
    // 전체 높이 - 화면 높이 - (화면 높이/2) 는 스크롤이 거의 모두 내려가서 현재 화면 높이의 반 보다 적게 스크롤 할 양이 남았을 경우를 의미한다.
    if (matchDetailWrap.scrollHeight - matchDetailWrap.clientHeight - (matchDetailWrap.clientHeight / 2)
      < matchDetailWrap.scrollTop) {
      endReachedHandler()
    }
  }

  // 빠르게 여러번 발생하는 이벤트는 스로틀로 0.2초에 한번만 처리한다.
  const throttleListScrollHandler = _.throttle(listScrollHandler, 200)

  let matchDetailWrap
  // 스크롤 이벤트 해제 시 dom 이 이미 제거되었을 수 있으므로 useOnUnmount 보다 componentWillUnmount 과 유사한 useEffect return 을 이용한다.
  useEffect(() => {
    matchDetailWrap = document.getElementById('matchDetailWrap')
    matchDetailWrap.addEventListener('scroll', throttleListScrollHandler, true)

    return () => {
      matchDetailWrap.removeEventListener('scroll', throttleListScrollHandler, true)
    }
  }, [])

  useEffect(() => {
    if (_.isNil(matchId)) {
      setPicks([])
      return
    }
    fetch(true).catch(console.error)
  }, [matchId])

  useEffect(() => {
    const cPicks: Schema$CompanyPick[] = _.isNil(companyPicks) ? [] : companyPicks
    const uPicks: Schema$UserPick[] = _.isNil(detailUserPicks) ? [] : detailUserPicks
    setPicks(_.concat(cPicks, uPicks as unknown))
  }, [detailUserPicks, companyPicks])

  const fetch = async (reset = false) => {
    if (reset) {
      setPicks([])
      companyPickStore.api.companyPick.fetchCompanyPicks({ matchId }).then(response => {
        companyPickStore.setCompanyPicks(_.sortBy(response.items, 'createdAt'))
      })
    }

    userPickStore.setDetailUserPickMarketFilter(EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK)
    userPickStore.fetchDetailUserPickMatchDetailPackage({ matchId, reset }).catch(console.error)
  }

  const endReachedHandler = async () => {
    if (userPickStoreRef.current.detailHasMoreUserPicks && !userPickStoreRef.current.isFetchUserPicksLoading) {
      userPickStoreRef.current.fetchDetailUserPicks(matchId).catch(console.error)
    }
  }

  const isUserPick = (item: Schema$CompanyPick | Schema$UserPick): item is Schema$UserPick => {
    return (item as Schema$UserPick)?.pickOwner !== undefined
  }

  const onUserBlockSuccessHandler = (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => {
    runInAction(() => {
      const isUserBlock = !_.isEmpty(blockTypes)
      const isUserPickBlock = _.includes(blockTypes, COMMUNICATION_BLOCK_TYPE.USER_PICK)

      userPickStore.detailUserPicks = userPickStore.setUserBlock(
        detailUserPicks as Schema$UserPickExtend[],
        userPromiseId,
        isUserBlock,
        isUserPickBlock,
      )
    })
  }

  const isMatchupPageCall = currentGameKey !== GAME_TYPE.LIVE && pageType !== PAGE_TYPE.MATCHUP_RESULT
    && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER

  const renderRow = (item: Schema$CompanyPick | Schema$UserPick) => {
    let content
    if (isUserPick(item)) {
      const recentHitRate = detailUserPickRecentHitRateMap?.[item.pickOwner.userPromiseId]
      content = <UserPickItem
        userPick={item}
        recentHitRate={recentHitRate}
        onUserBlockSuccessHandler={onUserBlockSuccessHandler}
      />
    } else {
      content =
        <CompanyPickLikeActivityListItem companyPick={item} isDisabledLikeButton={false} disableUserInfoClick={false}
                                         isMatchupPageCall={isMatchupPageCall}/>
    }
    return <Fragment>{content}</Fragment>
  }

  const renderFooter = () => {
    let footer
    if (_.size(picks) > 0) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.MATCHUP_DETAIL_PICK_LIST}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  return (
    <MatchUpDetailPickWrap>
      <ListView
        css={ListViewStyle}
        dataSource={dataSource.cloneWithRows(picks ?? [])}
        renderRow={renderRow}
        renderFooter={renderFooter}
        initialListSize={_.size(picks)}
        pageSize={_.size(picks)}
      />
      {pageType !== PAGE_TYPE.MATCHUP_RESULT
        && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER
        && currentGameKey !== GAME_TYPE.LIVE
        && <UserPickWriteButton fixed={isMobile}
                                className={!isMobile ? 'detail-bet-write-btn pc' : 'detail-bet-write-btn'}
                                fromView={FROM_VIEW_TYPE.MATCHUP_DETAIL_PICK}
                                pageType={PAGE_TYPE.MATCHUP_DETAIL_PICK}/>
      }
    </MatchUpDetailPickWrap>
  )
})
